import { type MutationMeta, useMutation } from '@tanstack/react-query';

import { endpoints } from '@/config/apiEndpoints';
import { collections } from '@/config/databasePaths';
import { STORAGE_ENABLED } from '@/config/env';
import { useActiveOrganizationId } from '@/features/auth/useSession';
import { brandHubQueryKey, byName } from '@/features/brandHub/brandHub';
import { type LiveCollection, useLiveCollection, useLiveMutation } from '@/hooks/useLiveData';
import { apiClient } from '@/lib/apiClient';
import { uploadObject } from '@/lib/firebase';
import type { PendingKind } from '@/lib/pendingWrites';
import { requireParsed } from '@/lib/readers';
import { ASSET_FOLDERS, brandHubObjectName, type UploadTarget } from './assets';
import {
  type AudioKind,
  type BrandAsset,
  type BrandAssetType,
  parseBrandAssetOf,
  parseBrandAssetResponse,
  type VisualElementKind,
} from './types';

const ASSET_LIMIT = 500;

/** Returns the assets of one type from the sub-collection of one brand, sorted by name. */
export function useBrandAssets(slug: string, assetType: BrandAssetType): LiveCollection<BrandAsset> {
  const organizationId = useActiveOrganizationId();

  const collection = useLiveCollection<BrandAsset>({
    path: collections.brandAssets(organizationId ?? '_', slug || '_'),
    httpPath: endpoints.brandHub.brand.assets.list(slug || '_'),
    queryKey: brandHubQueryKey('brand-assets', organizationId, slug, assetType),
    parse: parseBrandAssetOf(slug, assetType),
    limit: ASSET_LIMIT,
    filters: [['asset_type', '==', assetType]],
    enabled: Boolean(organizationId && slug),
  });

  return { ...collection, data: byName(collection.data, (asset) => asset.name) };
}

/** Stores one selected file and returns the object name to send to the API.
 *  If no bucket is configured, it does not upload the file. */
export function useUploadBrandHubFile(target: UploadTarget, meta: MutationMeta = { toastsError: false }) {
  const organizationId = useActiveOrganizationId();

  return useMutation({
    mutationFn: async (file: File) => {
      const objectName = brandHubObjectName(organizationId ?? '_', target, file.name);
      if (STORAGE_ENABLED) await uploadObject(objectName, file);
      return objectName;
    },
    meta,
  });
}

/** Stores the selected files one after the other and returns their object names. */
export function useUploadBrandHubFiles(target: UploadTarget) {
  const upload = useUploadBrandHubFile(target);

  return useMutation({
    mutationFn: async (files: File[]) => {
      const objectNames: string[] = [];
      for (const file of files) objectNames.push(await upload.mutateAsync(file));
      return objectNames;
    },
    meta: { errorKey: 'brandHub.uploadFailed' },
  });
}

export interface BrandAssetCreate {
  assetType: BrandAssetType;
  name: string;
  fileObject: string;
  kind?: AudioKind | VisualElementKind;
  isAiCreated?: boolean;
  hasWaiver?: boolean;
  tags?: string[];
}

/** An absent field is unchanged. */
export interface BrandAssetUpdate {
  name?: string;
  kind?: AudioKind | VisualElementKind;
  tags?: string[];
}

/** Sends one asset request and puts the asset in the response into the list of its
 *  brand and type, marked until the stored data shows it. */
function useAssetMutation<TVariables>(
  kind: PendingKind,
  send: (variables: TVariables) => Promise<unknown>,
  meta?: MutationMeta,
) {
  const organizationId = useActiveOrganizationId();
  return useLiveMutation({
    queryKey: (asset: BrandAsset) => brandHubQueryKey('brand-assets', organizationId, asset.brand, asset.assetType),
    kind,
    mutationFn: async (variables: TVariables) =>
      requireParsed(parseBrandAssetResponse(await send(variables)), 'brand asset'),
    rowOf: (asset: BrandAsset) => asset,
    meta,
  });
}

/** Stores one uploaded file as an asset of the brand. */
export function useCreateBrandAsset() {
  return useAssetMutation(
    'create',
    ({ slug, asset }: { slug: string; asset: BrandAssetCreate }) =>
      apiClient.post(endpoints.brandHub.brand.assets.create(slug), {
        asset_type: asset.assetType,
        name: asset.name,
        file_object: asset.fileObject,
        ...(asset.kind !== undefined && { kind: asset.kind }),
        ...(asset.isAiCreated !== undefined && { is_ai_created: asset.isAiCreated }),
        ...(asset.hasWaiver !== undefined && { has_waiver: asset.hasWaiver }),
        ...(asset.tags !== undefined && { tags: asset.tags }),
      }),
    { toastsError: false },
  );
}

/** Changes the name, the kind or the tags of one asset. */
export function useUpdateBrandAsset() {
  return useAssetMutation(
    'update',
    ({ slug, assetId, update }: { slug: string; assetId: string; update: BrandAssetUpdate }) =>
      apiClient.put(endpoints.brandHub.brand.assets.update(slug, assetId), {
        ...(update.name !== undefined && { name: update.name }),
        ...(update.kind !== undefined && { kind: update.kind }),
        ...(update.tags !== undefined && { tags: update.tags }),
      }),
  );
}

/** Removes one asset of the brand. */
export function useDeleteBrandAsset() {
  return useAssetMutation('delete', ({ slug, assetId }: { slug: string; assetId: string }) =>
    apiClient.delete(endpoints.brandHub.brand.assets.delete(slug, assetId)),
  );
}

/** Uploads the selected files to the folder of the brand and type, then stores each one
 *  as an asset. The default name is the file name. */
export function useAddBrandAssets(slug: string, assetType: BrandAssetType) {
  const upload = useUploadBrandHubFile({ brandSlug: slug, folder: ASSET_FOLDERS[assetType] });
  const create = useCreateBrandAsset();

  return useMutation({
    mutationFn: async ({
      files,
      ...fields
    }: Omit<BrandAssetCreate, 'assetType' | 'name' | 'fileObject'> & { files: File[] }) => {
      for (const file of files) {
        const fileObject = await upload.mutateAsync(file);
        await create.mutateAsync({
          slug,
          asset: { assetType, name: file.name, fileObject, ...fields },
        });
      }
    },
    meta: { errorKey: 'brandHub.uploadFailed' },
  });
}
